import type { Position } from "@xyflow/react";

export type Point = { x: number; y: number };

/**
 * Fast-path ortogonal routing. A*-slow-path kommer i Fas 3 (Web Worker).
 *
 * Regel:
 *  - Om |dx| < eps: rak vertikal linje.
 *  - Om |dy| < eps: rak horisontell linje.
 *  - Annars: välj mellan-waypoint baserat på source/target Position
 *    (top/bottom → dra ut vertikalt först; left/right → horisontellt först).
 *
 * Returnerar ordnad lista av waypoints från source till target (inklusive båda).
 */
export function computeOrthogonalPath(
  source: Point,
  target: Point,
  sourcePosition: Position,
  targetPosition: Position,
): Point[] {
  const eps = 0.5;
  const dx = target.x - source.x;
  const dy = target.y - source.y;

  if (Math.abs(dx) < eps) {
    return [source, target];
  }
  if (Math.abs(dy) < eps) {
    return [source, target];
  }

  const sourceVertical = sourcePosition === "top" || sourcePosition === "bottom";
  const targetVertical = targetPosition === "top" || targetPosition === "bottom";

  if (sourceVertical && targetVertical) {
    const midY = source.y + dy / 2;
    return [source, { x: source.x, y: midY }, { x: target.x, y: midY }, target];
  }
  if (!sourceVertical && !targetVertical) {
    const midX = source.x + dx / 2;
    return [source, { x: midX, y: source.y }, { x: midX, y: target.y }, target];
  }
  if (sourceVertical) {
    return [source, { x: source.x, y: target.y }, target];
  }
  return [source, { x: target.x, y: source.y }, target];
}

export function waypointsToSvgPath(points: Point[]): string {
  if (points.length === 0) return "";
  const [first, ...rest] = points;
  if (!first) return "";
  let d = `M ${first.x} ${first.y}`;
  for (const p of rest) {
    d += ` L ${p.x} ${p.y}`;
  }
  return d;
}

export function midpoint(points: Point[]): Point {
  if (points.length === 0) return { x: 0, y: 0 };
  if (points.length === 1) {
    const only = points[0];
    return only ?? { x: 0, y: 0 };
  }
  let totalLen = 0;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (!a || !b) continue;
    totalLen += Math.hypot(b.x - a.x, b.y - a.y);
  }
  const half = totalLen / 2;
  let acc = 0;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (!a || !b) continue;
    const seg = Math.hypot(b.x - a.x, b.y - a.y);
    if (acc + seg >= half) {
      const t = seg === 0 ? 0 : (half - acc) / seg;
      return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
    }
    acc += seg;
  }
  const last = points[points.length - 1];
  return last ?? { x: 0, y: 0 };
}

// --- Line breaking --------------------------------------------------------
// När två ledare korsar varandra utan elektrisk anslutning renderar vi en
// hop-båge på en av dem (den med lexikografiskt större id). Detta är visuell
// konvention enligt SS-EN-praxis — den elektriska kontinuiteten i domänen är
// orörd (Conductor.fromTerminal/toTerminal), bara SVG-path:en justeras.

export type Segment = { a: Point; b: Point };

export type EdgePathInput = { id: string; waypoints: Point[] };

export const EPS = 0.5;

export function segmentsFromWaypoints(points: Point[]): Segment[] {
  const out: Segment[] = [];
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (a && b) out.push({ a, b });
  }
  return out;
}

function intersectOrthogonal(sa: Segment, sb: Segment): Point | null {
  const saHoriz = Math.abs(sa.a.y - sa.b.y) < EPS;
  const saVert = Math.abs(sa.a.x - sa.b.x) < EPS;
  const sbHoriz = Math.abs(sb.a.y - sb.b.y) < EPS;
  const sbVert = Math.abs(sb.a.x - sb.b.x) < EPS;

  // Endast perpendikulära par genererar ett korsningsbeslut.
  if (saHoriz && sbVert) {
    const y = sa.a.y;
    const x = sb.a.x;
    const inA =
      x > Math.min(sa.a.x, sa.b.x) + EPS && x < Math.max(sa.a.x, sa.b.x) - EPS;
    const inB =
      y > Math.min(sb.a.y, sb.b.y) + EPS && y < Math.max(sb.a.y, sb.b.y) - EPS;
    if (inA && inB) return { x, y };
  }
  if (saVert && sbHoriz) {
    const x = sa.a.x;
    const y = sb.a.y;
    const inA =
      y > Math.min(sa.a.y, sa.b.y) + EPS && y < Math.max(sa.a.y, sa.b.y) - EPS;
    const inB =
      x > Math.min(sb.a.x, sb.b.x) + EPS && x < Math.max(sb.a.x, sb.b.x) - EPS;
    if (inA && inB) return { x, y };
  }
  return null;
}

/**
 * Given alla edges med beräknade waypoints, returnera en karta edgeId -> hop-punkter.
 * Endast den lexikografiskt "större" id:n får hoppet (deterministiskt).
 */
export function computeHops(edges: EdgePathInput[]): Map<string, Point[]> {
  const hopsByEdge = new Map<string, Point[]>();
  const segsByEdge = edges.map((e) => ({
    id: e.id,
    segs: segmentsFromWaypoints(e.waypoints),
  }));

  for (let i = 0; i < segsByEdge.length; i++) {
    for (let j = i + 1; j < segsByEdge.length; j++) {
      const A = segsByEdge[i];
      const B = segsByEdge[j];
      if (!A || !B) continue;
      for (const sa of A.segs) {
        for (const sb of B.segs) {
          const cross = intersectOrthogonal(sa, sb);
          if (!cross) continue;
          const hopper = A.id < B.id ? B.id : A.id;
          const arr = hopsByEdge.get(hopper) ?? [];
          arr.push(cross);
          hopsByEdge.set(hopper, arr);
        }
      }
    }
  }
  return hopsByEdge;
}

export function pointOnSegment(p: Point, seg: Segment): boolean {
  const horiz = Math.abs(seg.a.y - seg.b.y) < EPS;
  const vert = Math.abs(seg.a.x - seg.b.x) < EPS;
  if (horiz && Math.abs(p.y - seg.a.y) < EPS) {
    const lo = Math.min(seg.a.x, seg.b.x);
    const hi = Math.max(seg.a.x, seg.b.x);
    return p.x > lo + EPS && p.x < hi - EPS;
  }
  if (vert && Math.abs(p.x - seg.a.x) < EPS) {
    const lo = Math.min(seg.a.y, seg.b.y);
    const hi = Math.max(seg.a.y, seg.b.y);
    return p.y > lo + EPS && p.y < hi - EPS;
  }
  return false;
}

const HOP_RADIUS = 4;

/**
 * Som waypointsToSvgPath men infogar en halvcirkel-båge över varje hop-punkt
 * som ligger på en av segmenten. Sweep-riktningen är konsekvent (bågen buktar
 * "uppåt" för horisontella segment, "åt vänster" för vertikala).
 */
export function waypointsToSvgPathWithHops(
  points: Point[],
  hops: Point[] | undefined,
  hopRadius: number = HOP_RADIUS,
): string {
  if (points.length === 0) return "";
  const [first, ...rest] = points;
  if (!first) return "";
  if (!hops || hops.length === 0) return waypointsToSvgPath(points);

  let d = `M ${first.x} ${first.y}`;
  let prev = first;
  for (const next of rest) {
    const seg: Segment = { a: prev, b: next };
    const dx = next.x - prev.x;
    const dy = next.y - prev.y;
    const len = Math.hypot(dx, dy);
    if (len < EPS) {
      d += ` L ${next.x} ${next.y}`;
      prev = next;
      continue;
    }
    const ux = dx / len;
    const uy = dy / len;
    const segHops = hops
      .filter((h) => pointOnSegment(h, seg))
      .map((h) => ({
        h,
        dist: Math.hypot(h.x - prev.x, h.y - prev.y),
      }))
      .sort((a, b) => a.dist - b.dist);

    for (const { h } of segHops) {
      const pre = { x: h.x - ux * hopRadius, y: h.y - uy * hopRadius };
      const post = { x: h.x + ux * hopRadius, y: h.y + uy * hopRadius };
      d += ` L ${pre.x} ${pre.y}`;
      d += ` A ${hopRadius} ${hopRadius} 0 0 1 ${post.x} ${post.y}`;
    }
    d += ` L ${next.x} ${next.y}`;
    prev = next;
  }
  return d;
}
